// QuietHoursCard — toggle + start/end TimePickers. Notifications scheduled
// inside the window are silently held by the scheduler and dispatched once
// the window closes (no-loss, no-spam pattern).

import { useState } from 'react';
import { api, APIError } from '../lib/api';
import type { UserSettings } from '../lib/api';
import { Card } from './Card';
import { TimePicker, parseHM } from './TimePicker';

export function QuietHoursCard({
  settings,
  onChange,
}: {
  settings: UserSettings;
  onChange: (next: UserSettings) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = parseHM(settings.quiet_hours_start ?? '22:00') ?? { hour: 22, minute: 0 };
  const end = parseHM(settings.quiet_hours_end ?? '07:00') ?? { hour: 7, minute: 0 };

  const wraps = settings.quiet_hours_enabled
    ? hmToMin(start) > hmToMin(end)
    : false;

  const save = async (patch: Partial<UserSettings>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.patchUserSettings(patch);
      onChange(next);
    } catch (e) {
      setError(e instanceof APIError && typeof e.body === 'object' && e.body && 'error' in e.body
        ? String((e.body as { error: string }).error)
        : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (next: boolean) => {
    if (next) {
      await save({
        quiet_hours_enabled: true,
        quiet_hours_start: settings.quiet_hours_start || '22:00',
        quiet_hours_end: settings.quiet_hours_end || '07:00',
      });
    } else {
      await save({ quiet_hours_enabled: false });
    }
  };

  return (
    <Card title="Quiet hours" hint="Silence pings inside the window. They fire once the window closes.">
      <div className="flex items-center justify-between">
        <span className="text-sm text-fg">Enable quiet hours</span>
        <button
          type="button"
          role="switch"
          aria-checked={settings.quiet_hours_enabled}
          onClick={() => toggle(!settings.quiet_hours_enabled)}
          disabled={busy}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            settings.quiet_hours_enabled ? 'bg-accent' : 'bg-bg-sunken border border-line-strong'
          }`}
        >
          <span
            className={`inline-block size-5 rounded-full bg-bg-raised shadow-[0_1px_2px_rgba(11,11,15,0.24)] transform transition-transform ${
              settings.quiet_hours_enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {settings.quiet_hours_enabled && (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-fg-muted">Start</span>
            <TimePicker
              hour={start.hour}
              minute={start.minute}
              minuteStep={5}
              onChange={(h, m) =>
                save({ quiet_hours_start: `${pad(h)}:${pad(m)}` })
              }
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-fg-muted">End</span>
            <TimePicker
              hour={end.hour}
              minute={end.minute}
              minuteStep={5}
              onChange={(h, m) => save({ quiet_hours_end: `${pad(h)}:${pad(m)}` })}
            />
          </div>
          <p className="text-xs text-fg-faint">
            {wraps
              ? `Active ${pad(start.hour)}:${pad(start.minute)} → ${pad(end.hour)}:${pad(end.minute)} (next day)`
              : `Active ${pad(start.hour)}:${pad(start.minute)} → ${pad(end.hour)}:${pad(end.minute)}`}
          </p>
        </div>
      )}
      {error && <p className="text-xs text-danger">{error}</p>}
    </Card>
  );
}

function hmToMin(t: { hour: number; minute: number }): number {
  return t.hour * 60 + t.minute;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
